import {generateKeyBetween, generateNKeysBetween} from 'fractional-indexing';

import {type PanelConfigOverride} from '../../components/PanelBank/types';
import {getOverrideKey} from '../../util/panels';
import {type PanelConfig} from '../../util/panelTypes';
import {isSparseOverridesEnabled} from '../../util/sparseOverridesFeatureFlag';
import {setInShallowClone} from '../../util/utility';
import {lookupPart} from './normalizerSupport';
import type * as PanelTypes from './panel/types';
import * as ConfigOverrideUtils from './panelBankConfig/configOverrideUtils';
import type * as PanelBankConfigTypes from './panelBankConfig/types';
import type * as PanelBankSectionConfigTypes from './panelBankSectionConfig/types';
import type {ViewReducerState} from './reducerSupport';

export type ConfigOverrideInfo = {
  panelBankConfigRef: PanelBankConfigTypes.Ref;
  panelKey: string;
  prevOverride: PanelConfigOverride | undefined;
};

/**
 * Creates placement overrides for custom panels after they are added to a section.
 *
 * Custom panels (isAuto=false) need placement overrides so they can be positioned
 * correctly after reload. Without overrides, they would appear at the beginning
 * after decompression since their position isn't encoded anywhere else.
 *
 * @param state The current state (after panels have been added)
 * @param sectionRef The section the panels were added to
 * @param newPanelRefs The refs of the newly added panels
 * @returns Updated state with placement overrides for custom panels
 */
export function createPlacementOverridesForCustomPanels(
  state: ViewReducerState,
  sectionRef: PanelBankSectionConfigTypes.Ref,
  newPanelRefs: PanelTypes.Ref[],
  panelBankConfigRef?: PanelBankConfigTypes.Ref | null
): ViewReducerState {
  // Only create placement overrides when feature is enabled
  if (!isSparseOverridesEnabled()) {
    return state;
  }

  if (!panelBankConfigRef) {
    return state;
  }

  const panelBankConfig = lookupPart(state.parts, panelBankConfigRef);

  const sectionConfig = lookupPart(state.parts, sectionRef);
  const sectionId = sectionConfig.__id__;

  // Get current overrides
  const currentOverrides = panelBankConfig.panelPlacementOverrides ?? {};
  let newOverrides = {...currentOverrides};
  let hasNewOverrides = false;

  // Pre-compute a uniform grid of positional keys for the section.
  // On reload, the ordering logic merges these keys with freshly-generated
  // positional keys for auto panels and sorts lexicographically, preserving
  // each custom panel's relative position.
  const panelRefs = sectionConfig.panelRefs;
  const positionalKeys = generateNKeysBetween(null, null, panelRefs.length);

  // Process each new panel
  for (const panelRef of newPanelRefs) {
    const panel = lookupPart(state.parts, panelRef);

    // Only create overrides for custom panels (isAuto === false)
    if (panel.isAuto !== false) {
      continue;
    }

    const overrideKey = getOverrideKey(panel);
    if (!overrideKey) {
      continue; // Panel has no override key (shouldn't happen for custom panels)
    }

    // Find the panel's position in the section
    const panelIndex = panelRefs.findIndex(ref => ref.id === panelRef.id);

    if (panelIndex === -1) {
      continue;
    }

    // Slot the custom panel just before its grid position
    const orderKey = generateKeyBetween(
      panelIndex > 0 ? positionalKeys[panelIndex - 1] : null,
      positionalKeys[panelIndex]
    );

    // Create the override
    newOverrides[overrideKey] = {
      sectionId,
      orderKey,
    };
    hasNewOverrides = true;
  }

  // Update state with new overrides if any were created
  if (hasNewOverrides) {
    return setInShallowClone(
      state,
      [
        'parts',
        panelBankConfigRef.type,
        panelBankConfigRef.id,
        'panelPlacementOverrides',
      ],
      newOverrides
    );
  }

  return state;
}

/**
 * Checks eligibility and computes a sparse config override for a panel.
 * Returns the updated state (with override stored) + override info for undo,
 * or null if the panel is not eligible for sparse overrides.
 *
 * The caller is responsible for setting the panel config and isAuto in state.
 */
export function tryComputeSparseOverride(
  state: ViewReducerState,
  panelRef: PanelTypes.Ref,
  resolvedConfig: PanelConfig,
  panelBankConfigRef: PanelBankConfigTypes.Ref | undefined,
  panelBankSectionConfigRef?: PanelBankSectionConfigTypes.Ref
): {updatedState: ViewReducerState; overrideInfo: ConfigOverrideInfo} | null {
  if (panelBankConfigRef == null || !isSparseOverridesEnabled()) {
    return null;
  }

  const {eligible, panelKey} =
    ConfigOverrideUtils.isPanelEligibleForConfigOverride(
      state,
      panelRef,
      panelBankSectionConfigRef,
      panelBankConfigRef
    );
  if (!eligible || panelKey == null) {
    return null;
  }

  const pbConfig = state.parts[panelBankConfigRef.type][panelBankConfigRef.id];
  const currentOverrides = pbConfig.panelConfigOverrides ?? {};
  const prevOverride = currentOverrides[panelKey];

  const newOverrides = {
    ...currentOverrides,
    [panelKey]: ConfigOverrideUtils.mergeConfigOverride(
      prevOverride,
      resolvedConfig
    ),
  };

  const updatedState = setInShallowClone(
    state,
    [
      'parts',
      panelBankConfigRef.type,
      panelBankConfigRef.id,
      'panelConfigOverrides',
    ],
    newOverrides
  );

  return {
    updatedState,
    overrideInfo: {panelBankConfigRef, panelKey, prevOverride},
  };
}

/**
 * Swaps a config override to its previous value (for undo/redo support).
 * Returns the updated state + the override info for the inverse action.
 */
export function swapConfigOverride(
  state: ViewReducerState,
  overrideInfo: ConfigOverrideInfo
): {newState: ViewReducerState; prevOverrideForInverse: ConfigOverrideInfo} {
  const {panelBankConfigRef, panelKey, prevOverride} = overrideInfo;
  const pbConfig = state.parts[panelBankConfigRef.type][panelBankConfigRef.id];

  const currentOverrides = pbConfig.panelConfigOverrides ?? {};
  const currentOverride = currentOverrides[panelKey];

  const newOverrides = {...currentOverrides};
  if (prevOverride) {
    newOverrides[panelKey] = prevOverride;
  } else {
    delete newOverrides[panelKey];
  }

  const newState = setInShallowClone(
    state,
    [
      'parts',
      panelBankConfigRef.type,
      panelBankConfigRef.id,
      'panelConfigOverrides',
    ],
    Object.keys(newOverrides).length > 0 ? newOverrides : undefined
  );

  return {
    newState,
    prevOverrideForInverse: {
      panelBankConfigRef,
      panelKey,
      prevOverride: currentOverride,
    },
  };
}
